// Modal de import em lote de PDFs (upload paralelo + polling de jobs).
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Modal, Form, Input, Select, Row, Col, Button, Card, Upload, Tag, Space, Flex, Typography, App as AntApp,
} from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { CATEGORIAS } from './types';
import type { JobLocal } from './types';

const { Text } = Typography;

interface Props {
  onClose: () => void;
  onConcluido: () => void;
}

export default function ModalBatchImport({ onClose, onConcluido }: Props) {
  const { message } = AntApp.useApp();
  const [categoria, setCategoria] = useState('geral');
  const [tags, setTags] = useState('');
  const [jobs, setJobs] = useState<JobLocal[]>([]);
  const [iniciado, setIniciado] = useState(false);
  const [pollando, setPollando] = useState(false);

  useEffect(() => {
    if (!iniciado) return;
    const ids = jobs.filter(j => j.job_id && (j.status === 'fila' || j.status === 'extraindo')).map(j => j.job_id);
    if (ids.length === 0) { setPollando(false); return; }
    setPollando(true);
    const t = setInterval(async () => {
      try {
        const token = await getToken();
        const r = await axios.post(`${API_URL}/agentes/exemplos`,
          { action: 'jobs_status', ids },
          { headers: { Authorization: token } });
        const data = r.data.body ? JSON.parse(r.data.body) : r.data;
        if (!Array.isArray(data)) return;
        setJobs(prev => prev.map(p => {
          if (!p.job_id) return p;
          const srv = data.find((d: any) => d.id === p.job_id);
          if (!srv) return p;
          if (srv.status === 'completo') return { ...p, status: 'ok' as const, exemplo_id: srv.exemplo_id };
          if (srv.status === 'erro') return { ...p, status: 'erro' as const, erro: srv.erro };
          if (srv.status === 'extraindo') return { ...p, status: 'extraindo' as const };
          return p;
        }));
      } catch {}
    }, 4000);
    return () => clearInterval(t);
  }, [iniciado, jobs]);

  function adicionarArquivos(files: File[]) {
    const novos: JobLocal[] = [];
    for (const f of files) {
      if (!f.name.toLowerCase().endsWith('.pdf')) continue;
      if (f.size > 30 * 1024 * 1024) {
        message.warning(`"${f.name}" > 30MB, ignorado.`);
        continue;
      }
      novos.push({ arquivo: f, status: 'aguardando' });
    }
    setJobs(prev => [...prev, ...novos]);
  }

  async function processarUm(idx: number) {
    setJobs(prev => prev.map((j, i) => i === idx ? { ...j, status: 'uploading' } : j));
    try {
      const file = jobs[idx].arquivo;
      const token = await getToken();
      const r1 = await axios.post(`${API_URL}/agentes/exemplos`,
        { action: 'upload_url', filename: file.name },
        { headers: { Authorization: token } });
      const dataUrl = r1.data.body ? JSON.parse(r1.data.body) : r1.data;
      const { upload_url, s3_key } = dataUrl;
      await axios.put(upload_url, file, { headers: { 'Content-Type': 'application/pdf' }, timeout: 120000 });
      const tagsArr = tags.split(',').map(t => t.trim()).filter(Boolean);
      const r3 = await axios.post(`${API_URL}/agentes/exemplos`,
        { action: 'extrair_pdf_async', s3_key, filename: file.name,
          categoria, titulo: file.name.replace(/\.pdf$/i, ''), tags: tagsArr },
        { headers: { Authorization: token } });
      const data3 = r3.data.body ? JSON.parse(r3.data.body) : r3.data;
      setJobs(prev => prev.map((j, i) => i === idx ? { ...j, status: 'fila', job_id: data3.job_id } : j));
    } catch (e: any) {
      const msg = e.response?.data?.error || e.message || 'erro';
      setJobs(prev => prev.map((j, i) => i === idx ? { ...j, status: 'erro', erro: msg } : j));
    }
  }

  async function iniciar() {
    setIniciado(true);
    const indices = jobs.map((_, i) => i).filter(i => jobs[i].status === 'aguardando');
    const CONCURRENCY = 3;
    let cursor = 0;
    async function worker() {
      while (cursor < indices.length) {
        const i = indices[cursor++];
        await processarUm(i);
      }
    }
    await Promise.all(Array.from({ length: CONCURRENCY }, worker));
  }

  const totalProcessados = jobs.filter(j => j.status === 'ok' || j.status === 'erro').length;
  const totalSucesso = jobs.filter(j => j.status === 'ok').length;
  const todosTerminados = iniciado && jobs.length > 0 && totalProcessados === jobs.length;

  const statusTag = (status: JobLocal['status'], erro?: string) => {
    const cfg: Record<JobLocal['status'], { txt: string; color: string }> = {
      aguardando: { txt: 'aguardando', color: 'default' },
      uploading: { txt: '⏫ enviando', color: 'blue' },
      fila: { txt: '⏳ na fila', color: 'gold' },
      extraindo: { txt: '🤖 extraindo', color: 'blue' },
      ok: { txt: '✓ pronto', color: 'green' },
      erro: { txt: '✗ erro', color: 'red' },
    };
    const c = cfg[status];
    return <Tag color={c.color} title={status === 'erro' ? erro : ''}>{c.txt}</Tag>;
  };

  return (
    <Modal open onCancel={() => { if (!pollando) onClose(); }} destroyOnClose width={720}
      title="Importar PDFs em lote"
      footer={
        !iniciado ? (
          <Space>
            <Button onClick={onClose}>Cancelar</Button>
            <Button type="primary" disabled={jobs.length === 0} onClick={iniciar}>
              Importar {jobs.length > 0 ? `(${jobs.length})` : ''}
            </Button>
          </Space>
        ) : (
          <Button type="primary" onClick={() => { onConcluido(); onClose(); }}>
            {todosTerminados ? 'Fechar' : 'Fechar (continua no servidor)'}
          </Button>
        )
      }>
      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 14 }}>
        Cada PDF vira um exemplo. Extração via Bedrock leva 30-90s por PDF. Pode fechar — continua no servidor.
      </Text>

      {!iniciado && (
        <>
          <Row gutter={10} style={{ marginBottom: 12 }}>
            <Col span={8}>
              <Form.Item label="Categoria" style={{ marginBottom: 0 }}>
                <Select value={categoria} onChange={setCategoria} size="small"
                  options={CATEGORIAS.map(c => ({ label: c, value: c }))} />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item label="Tags comuns (opcional)" style={{ marginBottom: 0 }}>
                <Input size="small" value={tags} onChange={e => setTags(e.target.value)}
                  placeholder="Ex.: sema, batimetria, RS" />
              </Form.Item>
            </Col>
          </Row>
          <Upload.Dragger accept="application/pdf" multiple={true} showUploadList={false}
            beforeUpload={(_, fileList) => { adicionarArquivos(fileList as any); return false; }}>
            <p><FilePdfOutlined style={{ fontSize: 32, color: '#0930D0' }} /></p>
            <Text strong style={{ color: '#0930D0' }}>Clique ou arraste PDFs</Text>
            <div><Text type="secondary" style={{ fontSize: 11 }}>Vários de uma vez · até 30MB cada</Text></div>
          </Upload.Dragger>
        </>
      )}

      {jobs.length > 0 && (
        <Card size="small" style={{ marginTop: 12, maxHeight: 300, overflow: 'auto' }} bodyStyle={{ padding: 0 }}>
          {jobs.map((j, idx) => (
            <Flex key={idx} align="center" gap={8} style={{
              padding: '8px 12px', fontSize: 12,
              borderBottom: idx < jobs.length - 1 ? '0.5px solid #f0f0f0' : 'none',
            }}>
              <Text style={{ flex: 1 }} ellipsis>
                📄 {j.arquivo.name}
                <Text type="secondary" style={{ marginLeft: 6, fontSize: 10 }}>
                  ({(j.arquivo.size / 1024 / 1024).toFixed(1)}MB)
                </Text>
              </Text>
              {statusTag(j.status, j.erro)}
              {!iniciado && (
                <Button size="small" type="text" onClick={() => setJobs(prev => prev.filter((_, i) => i !== idx))}>×</Button>
              )}
            </Flex>
          ))}
        </Card>
      )}

      {iniciado && (
        <Text type="secondary" style={{ fontSize: 11, display: 'block', textAlign: 'center', marginTop: 12 }}>
          {totalProcessados} de {jobs.length} processados · {totalSucesso} importados com sucesso
        </Text>
      )}
    </Modal>
  );
}
